/**
 * Utility functions for date parsing and validation
 */

/**
 * Parses a date string and returns a Date object or null if invalid
 * Handles various date formats commonly used in the application
 */
export const parseDate = (dateString: string | null | undefined): Date | null => {
  if (!dateString || dateString.trim() === "") {
    return null;
  }

  const trimmed = dateString.trim();

  // Try parsing as ISO date (YYYY-MM-DD)
  const isoDate = new Date(trimmed);
  if (!isNaN(isoDate.getTime())) {
    return isoDate;
  }

  // Try parsing formats like DD.MM.YYYY or DD-MM-YYYY
  const dateParts = trimmed.split(/[.\-/\s]/);
  if (dateParts.length === 3) {
    const [first, second, third] = dateParts.map(Number);

    // Check if first part is year (YYYY-MM-DD format)
    if (first > 31) {
      const date = new Date(first, second - 1, third);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }

    // Check if third part is year (DD-MM-YYYY or DD.MM.YYYY format)
    if (third > 31) {
      const date = new Date(third, second - 1, first);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }

  return null;
};

/**
 * Parses a date range string and returns start and end dates
 * Handles formats like:
 * - 01-03.01.2025 (day range in same month)
 * - 27.02-02.03.2025 (range across months, same year)
 * - 25.12.2025-06.01.2026 (full date range)
 *
 * Returns object with startDate and endDate, or null if parsing fails
 */
export interface DateRange {
  startDate: Date;
  endDate: Date;
}

export const parseDateRange = (dateString: string | null | undefined): DateRange | Date | null => {
  if (!dateString || dateString.trim() === "") {
    return null;
  }

  const trimmed = dateString.trim();

  // Check if it's a range (contains dash/hyphen)
  if (!trimmed.includes("-")) {
    // Not a range, try parsing as single date
    return parseDate(trimmed);
  }

  // Split by dash to get start and end parts
  const parts = trimmed.split("-");
  if (parts.length !== 2) {
    // Multiple dashes, try parsing as single date
    return parseDate(trimmed);
  }

  const [startPart, endPart] = parts.map((p) => p.trim());

  // Format 1: DD-DD.MM.YYYY (e.g., "01-03.01.2025")
  // Start: DD, End: DD.MM.YYYY
  const format1Match = startPart.match(/^(\d{1,2})$/);
  if (format1Match) {
    const startDay = parseInt(format1Match[1], 10);
    const endMatch = endPart.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (endMatch) {
      const [, endDay, month, year] = endMatch.map(Number);
      const startDate = new Date(year, month - 1, startDay);
      const endDate = new Date(year, month - 1, endDay);
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        return { startDate, endDate };
      }
    }
  }

  // Format 2: DD.MM-DD.MM.YYYY (e.g., "27.02-02.03.2025")
  // Start: DD.MM, End: DD.MM.YYYY
  const format2StartMatch = startPart.match(/^(\d{1,2})\.(\d{1,2})$/);
  if (format2StartMatch) {
    const [, startDay, startMonth] = format2StartMatch.map(Number);
    const endMatch = endPart.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (endMatch) {
      const [, endDay, endMonth, year] = endMatch.map(Number);
      const startDate = new Date(year, startMonth - 1, startDay);
      const endDate = new Date(year, endMonth - 1, endDay);
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        return { startDate, endDate };
      }
    }
  }

  // Format 3: DD.MM.YYYY-DD.MM.YYYY (e.g., "25.12.2025-06.01.2026")
  // Both parts are full dates
  const format3StartMatch = startPart.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (format3StartMatch) {
    const [, startDay, startMonth, startYear] = format3StartMatch.map(Number);
    const endMatch = endPart.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (endMatch) {
      const [, endDay, endMonth, endYear] = endMatch.map(Number);
      const startDate = new Date(startYear, startMonth - 1, startDay);
      const endDate = new Date(endYear, endMonth - 1, endDay);
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        return { startDate, endDate };
      }
    }
  }

  // If range parsing failed, try parsing as single date
  return parseDate(trimmed);
};

/**
 * Compares two date strings for sorting
 * For date ranges, uses the start date for comparison
 * Returns:
 * - negative if dateA < dateB
 * - positive if dateA > dateB
 * - 0 if equal or both invalid
 */
export const compareDates = (
  dateA: string | null | undefined,
  dateB: string | null | undefined,
): number => {
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1; // Put items without date at the end
  if (!dateB) return -1;

  const parsedA = parseDateRange(dateA);
  const parsedB = parseDateRange(dateB);

  if (!parsedA && !parsedB) return 0;
  if (!parsedA) return 1;
  if (!parsedB) return -1;

  // Extract start date from range or use single date
  const dateAValue = parsedA instanceof Date ? parsedA : parsedA.startDate;
  const dateBValue = parsedB instanceof Date ? parsedB : parsedB.startDate;

  return dateBValue.getTime() - dateAValue.getTime(); // Newest first
};
